import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import logo from '../assets/images/pulso-logo.png';
import { LEGAL } from './legal/legalDetails';
import '../styles/pulso.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function IconGlobe() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f0856d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z"/></svg>;
}
function IconTrend() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f0856d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>;
}
function IconBell() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f0856d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>;
}
function IconLock() {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f0856d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;
}

export default function LandingPage() {
  const revealRefs = useRef([]);
  const [demoSent, setDemoSent] = useState(false);
  const [demoForm, setDemoForm] = useState({ name: '', email: '', org: '', context: '' });
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const [demoError, setDemoError] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('p-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    revealRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const r = (el) => { if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el); };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleDemoSubmit = async () => {
    if (!demoForm.name || !demoForm.email) return;
    if (demoSubmitting) return;

    setDemoSubmitting(true);
    setDemoError('');

    try {
      const res = await fetch(`${API_URL}/api/demo/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(demoForm),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Something went wrong. Please try again.');
      }

      setDemoSent(true);
    } catch (err) {
      setDemoError(err.message || 'Could not send your request. Please try again.');
    } finally {
      setDemoSubmitting(false);
    }
  };

  return (
    <div className="lp-root">

      {/* ── NAV ──────────────────────────────────────────────────────────── */}
      <nav className="lp-nav">
        {/* Shared nav logo — also used in DashboardLayout */}
        <a href="/" className="nav-logo">
          <img src={logo} alt="Pulso" className="nav-logo-img" style={{ width: 36 }} />
          <div className="nav-logo-text-wrap">
            <span className="nav-logo-name">PULSO</span>
            <span className="nav-logo-by">by Imaginarium</span>
          </div>
        </a>
        <div className="lp-nav-right">
          <button className="lp-nav-link" onClick={() => scrollTo('how')}>
            How it works
          </button>
          <button className="p-btn-coral" onClick={() => scrollTo('demo')}>
            Book a demo
          </button>
          <Link to="/login" className="p-btn-outline">Admin</Link>
        </div>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="lp-hero">
        <div className="lp-hero-inner">
          <div className="lp-overline">Community Pulse Monitor</div>
          <h1 className="lp-h1">Every voice,<br /><em>understood.</em></h1>
          <p className="lp-hero-sub">
            Community engagement is broken. Organisations listen in one language, communities speak in another.
            Organisations schedule meetings, communities need to be heard right now. PULSO bridges the divide —
            turning WhatsApp messages, voicenotes, and SMS in any language into structured insight your teams
            can act on, and keeping the conversation alive.
          </p>
          <div className="lp-hero-actions">
            <Link to="/contact" className="p-btn-coral" style={{ fontSize: '1rem', padding: '1rem 2.2rem' }}>
              Send a message →
            </Link>
            <button className="p-btn-outline" style={{ fontSize: '1rem', padding: '1rem 2.2rem' }}
              onClick={() => scrollTo('demo')}>
              Book a demo
            </button>
          </div>
        </div>
      </section>

      {/* ── PULSE LINE ───────────────────────────────────────────────────── */}
      <div className="lp-divider">
        <svg viewBox="0 0 1200 56" preserveAspectRatio="none">
          <path className="lp-pulse-line"
            d="M0,28 L120,28 L150,8 L180,48 L210,8 L240,48 L270,28 L340,28 L370,8 L400,48 L430,8 L460,48 L490,28 L600,28 L630,8 L660,48 L690,28 L1200,28" />
        </svg>
      </div>

      {/* ── PROBLEM ──────────────────────────────────────────────────────── */}
      <section className="lp-problem">
        {/* Left col: label + heading + body */}
        <div ref={r} className="p-reveal">
          <span className="p-label">The barrier</span>
          <h2 className="lp-h2">The people most affected are the least heard.</h2>
          <p className="lp-body">
            In mining regions across Africa, community members navigate complex relationships
            with organisations that shape their livelihoods. But to raise a concern or ask a
            question, they're expected to do it in English, through formal channels, on someone
            else's terms.
          </p>
          <br />
          <p className="lp-body">
            That's not a communication system. That's a filter — and it's filtering out exactly
            the voices organisations need to hear.
          </p>
        </div>

        {/* Right col: stats — margin-top pushes them down to align with the h2
            p-label is ~0.68rem font + 1.25rem margin-bottom ≈ 2.1rem offset */}
        <div ref={r} className="p-reveal lp-stat-list" style={{ marginTop: '2.1rem' }}>
          {[
            'Thousands of languages across Africa — most corporate channels accept only one.',
            'Most community grievances never reach the systems designed to address them.',
            'Issues surface months after they started — long past the window for early action.',
            'The people with the most at stake have the least access to the conversation.',
          ].map((s, i) => <div key={i} className="lp-stat">{s}</div>)}
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────────── */}
      <section className="lp-how" id="how">
        <div className="lp-how-inner">
          <span ref={r} className="p-reveal p-label">How it works</span>
          <h2 ref={r} className="p-reveal lp-h2">Their language. Their channel.<br />Your insight.</h2>
          <p ref={r} className="p-reveal lp-how-sub">
            PULSO meets people where they are — no apps to download, no forms to fill, no new language to learn.
          </p>
          <div className="lp-steps">
            {[
              { n: '01', title: 'Community speaks',            body: "A community member sends a WhatsApp message, voice note, SMS, or email — in Zulu, Malagasy, Portuguese, Lingala, or any language they're comfortable with." },
              { n: '02', title: 'PULSO translates & analyses', body: 'Messages are translated, categorised by theme, and scored for sentiment automatically. Urgent issues are flagged immediately.' },
              { n: '03', title: 'Organisation responds',       body: 'Your team sees a dashboard of translated messages and sentiment trends. Respond in English — the community member gets the reply in their own language.' },
            ].map((step) => (
              <div key={step.n} ref={r} className="p-reveal lp-step">
                <div className="lp-step-num">{step.n}</div>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CAPABILITIES ─────────────────────────────────────────────────── */}
      <section className="lp-caps">
        <div ref={r} className="p-reveal lp-caps-header">
          <span className="p-label">For organisations</span>
          <h2 className="lp-h2">Intelligence, not just translation.</h2>
          <p className="lp-body">PULSO doesn't just move words between languages. It surfaces what matters, before it escalates.</p>
        </div>
        <div className="lp-caps-grid">
          {[
            { Icon: IconGlobe, title: 'Multilingual by default',    body: 'Support for African languages including Zulu, Xhosa, Sotho, Tswana, Malagasy, Swahili, Lingala, French, and Portuguese — with more added continuously.' },
            { Icon: IconTrend, title: 'Sentiment & trend analysis', body: 'See how community feeling shifts over time. Detect emerging concerns before they become crises. Spot patterns across hundreds of messages.' },
            { Icon: IconBell,  title: 'Urgent issue alerts',        body: 'Critical messages — safety incidents, environmental concerns, security threats — are flagged and escalated immediately through your preferred channel.' },
            { Icon: IconLock,  title: 'Confidential & safe',        body: 'Community members control their anonymity. Messages are encrypted in transit. No personal data is shared without explicit consent.' },
          ].map(({ Icon, title, body }) => (
            <div key={title} ref={r} className="p-reveal lp-cap">
              <div className="lp-cap-icon"><Icon /></div>
              <h3>{title}</h3>
              <p>{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CHANNELS ─────────────────────────────────────────────────────── */}
      <section className="lp-channels">
        <div className="lp-channels-inner">
          <div ref={r} className="p-reveal">
            <span className="p-label">Channels</span>
            <h2 className="lp-h2">Meet people where they are.</h2>
            <p className="lp-channels-sub">No app to download. No account to create. Just the tools they already use.</p>
            <div className="lp-channel-row">
              {['WhatsApp', 'SMS', 'Voice notes', 'Email', 'Web form'].map((c) => (
                <div key={c} className="lp-channel">
                  <span className="lp-channel-dot" />
                  {c}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA — top of the navy band ───────────────────────────────────── */}
      <section className="lp-cta">
        <div className="lp-cta-inner">
          <h2 ref={r} className="p-reveal lp-h2">See what it feels like<br />to be <em>heard.</em></h2>
          <p ref={r} className="p-reveal">
            Send a test message in any language — WhatsApp, email, or right here on the web.
            See PULSO in action in under 30 seconds.
          </p>
          <div ref={r} className="p-reveal lp-cta-actions">
            <Link to="/contact" className="p-btn-coral">Send a web message →</Link>
            <button className="p-btn-outline p-btn-outline-light" onClick={() => scrollTo('demo')}>
              Book a demo
            </button>
          </div>
          <p className="lp-cta-note">No signup required. Send a message and watch it arrive, translated and analysed.</p>
        </div>
      </section>

      {/* ── DEMO FORM — full navy ────────────────────────────────────────── */}
      <section className="lp-demo" id="demo">
        <div className="lp-demo-inner">
          <div ref={r} className="p-reveal">
            <span className="p-label">Get in touch</span>
            <h2 className="lp-h2">See PULSO in action.</h2>
            <p className="lp-demo-sub">
              Tell us about your context and we'll set up a personalised walkthrough — including
              the admin dashboard and live message flow.
            </p>
          </div>

          {demoSent ? (
            <div className="lp-form-success" style={{ color: '#ffffff' }}>
              <div className="lp-success-circle">✓</div>
              <h3 style={{ color: '#ffffff' }}>We've got your message.</h3>
              <p style={{ color: '#ffffff' }}>We'll be in touch to set up your demo.</p>
            </div>
          ) : (
            <div ref={r} className="p-reveal">
              <div className="lp-form-row p-field">
                <div className="p-field">
                  <label className="p-label-text">Your name</label>
                  <input className="p-input" placeholder="e.g. Thandi Moyo"
                    value={demoForm.name}
                    onChange={e => setDemoForm(p => ({ ...p, name: e.target.value }))} />
                </div>
                <div className="p-field">
                  <label className="p-label-text">Email</label>
                  <input className="p-input" type="email" placeholder="thandi@company.com"
                    value={demoForm.email}
                    onChange={e => setDemoForm(p => ({ ...p, email: e.target.value }))} />
                </div>
              </div>
              <div className="p-field">
                <label className="p-label-text">Organisation</label>
                <input className="p-input" placeholder="e.g. ACME Mining"
                  value={demoForm.org}
                  onChange={e => setDemoForm(p => ({ ...p, org: e.target.value }))} />
              </div>
              <div className="p-field">
                <label className="p-label-text">Tell us briefly about your context</label>
                <textarea className="p-textarea" rows={4}
                  placeholder="e.g. We operate three mine sites in Limpopo and struggle to get consistent community feedback in Sepedi and Tshivenda..."
                  value={demoForm.context}
                  onChange={e => setDemoForm(p => ({ ...p, context: e.target.value }))} />
              </div>
              <button
                className="lp-demo-submit"
                onClick={handleDemoSubmit}
                disabled={demoSubmitting}
                style={demoSubmitting ? { opacity: 0.6, cursor: 'wait' } : undefined}
              >
                {demoSubmitting ? 'Sending…' : 'Request a demo →'}
              </button>
              {demoError && (
                <p style={{ color: '#f0856d', marginTop: '0.75rem', fontSize: '0.9rem' }}>
                  {demoError}
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────────────── */}
      <footer className="lp-footer">
        <a href="/" className="nav-logo">
          <img src={logo} alt="Pulso" className="nav-logo-img" style={{ width: 28 }} />
          <div className="nav-logo-text-wrap">
            <span className="nav-logo-name">PULSO</span>
            <span className="nav-logo-by">by Imaginarium</span>
          </div>
        </a>
        <div className="lp-footer-right">
          <Link to="/about">About</Link>
          &nbsp;·&nbsp; <Link to="/privacy">Privacy</Link>
          &nbsp;·&nbsp; <Link to="/terms">Terms</Link>
          &nbsp;·&nbsp; <Link to="/data-deletion">Data deletion</Link>
          &nbsp;·&nbsp; <a href="https://imgnrm.com" target="_blank" rel="noreferrer">IMGNRM</a>
          &nbsp;·&nbsp; © {new Date().getFullYear()} {LEGAL.companyName}
          <span className="lp-footer-operator">
            Pulso is operated by {LEGAL.companyName}, the parent company of Imaginarium.
          </span>
        </div>
      </footer>

    </div>
  );
}